/**
 * Embedding:
 * https://help.gooddata.com/doc/enterprise/en/expand-your-gooddata-platform/gooddata-integration-into-your-application/embed-gooddata-elements-into-your-applications/embed-a-dashboard/
 *
 * Embed communication:
 * https://help.gooddata.com/doc/enterprise/en/expand-your-gooddata-platform/gooddata-integration-into-your-application/embed-gooddata-elements-into-your-applications/embed-a-dashboard/communication-with-embedded-dashboards/
 */

import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useLoggerService } from '@mews/framework';
import { getToken, ZIndex } from '@optimus-web/common';
import { Spinner } from '@optimus-web/core';
import { MewsReportingTracking, MewsReportingTrackingEvents } from '../const';
import { useMeasurePerformance } from '../hooks';
import { CustomFilterContext, GoodDataParameters } from '../types';

const LoadingOverlay = styled.div`
    position: absolute;
    display: flex;
    justify-content: center;
    align-items: center;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: ${ZIndex.Highest};
    background-color: ${({ theme }) => getToken('backgroundInteractiveNeutralSubtleDefault')({ theme })};
`;

export interface GDC {
    event: {
        contextId: string;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        data: any;
        name: string;
    };
    productName: string;
}

interface GoodDataEmbedProps {
    customFilterContext?: CustomFilterContext;
    goodDataParameters: GoodDataParameters;
    hideTopBar?: boolean;
    onMessage?: (gdc: GDC, contentWindow?: Window | null) => void;
    path: string;
    withLoader?: boolean;
}

export const GoodDataEmbed = ({
    customFilterContext,
    goodDataParameters,
    path,
    hideTopBar = false,
    onMessage,
    withLoader = false,
}: GoodDataEmbedProps) => {
    const logger = useLoggerService();
    const [isLoading, setIsLoading] = useState(true);
    const iframeRef = useRef<HTMLIFrameElement>(null);
    const { startTracking, endTracking, getTimeToLoad } = useMeasurePerformance();

    useEffect(() => {
        startTracking();
    }, [startTracking]);

    const handleFilterChange = () => {
        endTracking();
        const timeToLoad = getTimeToLoad();
        logger.log(MewsReportingTracking, MewsReportingTrackingEvents.EmbeddedReportServiceFilterChanged, {
            path,
            timeToLoad,
        });
    };

    useEffect(() => {
        if (!goodDataParameters) return;
        const contentWindow = iframeRef.current?.contentWindow;
        const origin = '*';

        const listener = (event: MessageEvent) => {
            if (event.data.gdc?.event.name === 'listeningForApiToken') {
                const postMessageStructure = {
                    gdc: {
                        product: 'dashboard',
                        event: {
                            name: 'setApiToken',
                            data: {
                                token: goodDataParameters.userToken,
                            },
                        },
                    },
                };

                contentWindow?.postMessage(postMessageStructure, origin);
            }

            if (customFilterContext && event.data.gdc?.event.name === 'loadingStarted') {
                const postMessageStructure = {
                    gdc: {
                        product: 'dashboard',
                        event: {
                            name: 'setFilterContext',
                            data: customFilterContext,
                        },
                    },
                };
                contentWindow?.postMessage(postMessageStructure, origin);
                handleFilterChange();
            }

            if (event.data.gdc?.event.name === 'loadingStarted') {
                setIsLoading(false);
            }

            if (event.data.gdc) onMessage?.(event.data.gdc, contentWindow);
        };

        window.addEventListener('message', listener, false);

        return () => window.removeEventListener('message', listener, false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [goodDataParameters]);

    return goodDataParameters ? (
        <>
            {withLoader && isLoading ? (
                <LoadingOverlay>
                    <Spinner />
                </LoadingOverlay>
            ) : null}
            <iframe
                ref={iframeRef}
                title="Embed GD Application"
                id="embedded-app-frame"
                src={`${goodDataParameters.baseUrl}${path}?apiTokenAuthentication=true&filtersSave=false${
                    hideTopBar ? '&hideControl=[topBar]' : ''
                }`}
                width="100%"
                height="100%"
                style={{ border: 0, overflow: 'hidden' }}
            />
        </>
    ) : null;
};
